import {
  AppPushNotificationsProps,
  AppPushPermissionStatus,
} from '@common/core/capacitor/app-push-notifications/assets/app-push-notifications-interface';
import { FirebaseMessaging } from '@capacitor-firebase/messaging';
import { getBootstrapData, mergeBootstrapData } from '@ui/bootstrap-data/bootstrap-data-store';
import { message } from '@ui/i18n/message';
import { toast } from '@ui/toast/toast';
import { AppPushRouter } from '@common/core/capacitor/app-push-notifications-manager';
import { NativeSettings, AndroidSettings, IOSSettings } from 'capacitor-native-settings';

interface NotificationCustomData {
  url?: string;
  title?: string;
  cta?: string;
}

function extractNotificationData(payload: unknown): NotificationCustomData {
  if (!payload) return {};
  try {
    return (payload as any)?.data ?? (payload as any)?.notification?.data ?? {};
  } catch {
    return {};
  }
}

function normalizePermissionStatus(
  status?: string | null,
): AppPushPermissionStatus {
  switch (status) {
    case 'granted':
      return 'granted';
    case 'denied':
      return 'denied';
    case 'prompt':
      return 'prompt';
    case 'prompt-with-rationale':
      return 'prompt-with-rationale';
    default:
      return 'unknown';
  }
}

export class AppPushNotificationsMobile implements AppPushNotificationsProps {
  private fcmToken: string | null = null;
  private tools: Record<string, any> = {};
  public notificationIsSupported: boolean = true;

  private readonly STORAGE_KEY_TOKEN = 'pn-token';
  private readonly STORAGE_KEY_REQUESTED_PERMISSION = 'pn-requested-permission';

  constructor(tools: Record<string, any>) {
    this.tools = tools;
  }

  public async loadManager(): Promise<void> {
    // sur mobile, on part du principe que le plugin est toujours présent
    // la valeur `notificationIsSupported` peut cependant être désactivée dynamiquement
    this.notificationIsSupported = true;
    return Promise.resolve();
  }

  public async init(): Promise<void> {
    if (!this.notificationIsSupported) return;
    
    const granted = await this.hasPermission();
    if (!granted) return;

    const token = await this.getToken();
    if (!token) return;

    this.setRequestedPermission();
    this.fcmToken = token;
    
    const storedToken = localStorage.getItem(this.STORAGE_KEY_TOKEN);
    if (token !== storedToken) {
      await this.tools.storeToken(token);
      localStorage.setItem(this.STORAGE_KEY_TOKEN, token);
    }

    this.handleMessages();
  }

  public async uninit(): Promise<void> {
    if (!this.notificationIsSupported) return;

    const token = this.fcmToken ?? localStorage.getItem(this.STORAGE_KEY_TOKEN);
    
    if (token) {
      await FirebaseMessaging.deleteToken();
      await this.tools.deleteToken(token);
      this.setRequestedPermission(false);
      localStorage.removeItem(this.STORAGE_KEY_TOKEN);
      this.fcmToken = null;
    }
    
    FirebaseMessaging.removeAllListeners();
  }

  public async syncToken(): Promise<void> {
    if (!this.notificationIsSupported) return;

    const hasPerm = await this.hasPermission();
    if (!hasPerm) return;

    const result = await FirebaseMessaging.getToken();
    const token = result?.token;
    if (!token) return;

    const storedToken = localStorage.getItem(this.STORAGE_KEY_TOKEN);
    if (token !== storedToken) {
      await this.tools.storeToken(token);
      localStorage.setItem(this.STORAGE_KEY_TOKEN, token);
    }

    this.fcmToken = token;
  }

  public async requestPermission(): Promise<Record<string, any>> {
    if (!this.notificationIsSupported) return {granted: false, status: "unsupported"};

    const status = await FirebaseMessaging.requestPermissions();
    this.setRequestedPermission(true);

    return {
      granted: status.receive === 'granted',
      status: normalizePermissionStatus(status.receive),
    };
  }

  public async hasPermission(): Promise<boolean | null | undefined> {
    if (!this.notificationIsSupported) return null;

    return (await this.getPermissionStatus()) === 'granted';
  }

  public async getPermissionStatus(): Promise<AppPushPermissionStatus> {
    if (!this.notificationIsSupported) return 'unsupported';

    const status = await FirebaseMessaging.checkPermissions();
    return normalizePermissionStatus(status.receive);
  }

  public async hasRequestedPermission(): Promise<boolean | null | undefined> {
    if (!this.notificationIsSupported) return null;
    return localStorage.getItem(this.STORAGE_KEY_REQUESTED_PERMISSION) === '1';
  }

  public async setRequestedPermission(value: boolean = true): Promise<void> {
    if (!this.notificationIsSupported) return;
    localStorage.setItem(this.STORAGE_KEY_REQUESTED_PERMISSION, value ? '1' : '0');
  }

  public async getToken(): Promise<string | null | undefined> {
    if (!this.notificationIsSupported) return null;

    if (this.fcmToken) return this.fcmToken;

    const result = await FirebaseMessaging.getToken();
    const token = result?.token;
    this.fcmToken = token ?? null;
    return token;
  }

  public async openConfig(): Promise<void> {
    NativeSettings.open({
      optionAndroid: AndroidSettings.ApplicationDetails, 
      optionIOS: IOSSettings.App
    });
    Promise.resolve();
  }

  private navigateTo(url: string): void {
    if (AppPushRouter.navigate) {
      try {
        AppPushRouter.navigate(url);
      } catch {
        window.location.href = url;
      }
    } else {
      window.location.href = url;
    }
  }

  /**
   * Gérer les messages reçus quand l'application est au foreground 
   * Et gestion des actions lorsque l'utilisateur clique sur une notification
   */
  private handleMessages(): void {
    FirebaseMessaging.removeAllListeners().then(() => {

      FirebaseMessaging.addListener('tokenReceived', async ({ token }) => {
        this.notificationIsSupported = true;
        this.fcmToken = token;
        await this.syncToken();
      });

      FirebaseMessaging.addListener('notificationReceived', async (payload) => {
        const data = extractNotificationData(payload);
        const newNotificationCount = await this.tools.getUnreadNotifications();
        const bootstrapData = getBootstrapData();
        const notificationTitle =
          payload.notification?.title ||
          data?.title ||
          'Hey, check this out!';

        const notificationAction = data?.url
          ? {
              action: data.url,
              label: data?.cta || 'View details',
            }
          : undefined;

        if (newNotificationCount) {
          mergeBootstrapData({
            user: {
              ...bootstrapData.user!,
              unread_notifications_count: newNotificationCount,
            },
          });
        }

        toast(message(notificationTitle), {
          type: 'notify',
          action: notificationAction,
          duration: 5000,
          position: 'bottom-center',
        });
      });

      FirebaseMessaging.addListener('notificationActionPerformed', async (payload) => {
        const data = extractNotificationData(payload);

        // Optionnel : update du badge si besoin
        const newNotificationCount = await this.tools.getUnreadNotifications();
        const bootstrapData = getBootstrapData();

        if (newNotificationCount) {
          mergeBootstrapData({
            user: {
              ...bootstrapData.user!,
              unread_notifications_count: newNotificationCount,
            },
          });
        }

        // Redirection vers l’URL définie dans la notif
        if (data.url) {
          this.navigateTo(data.url);
        }
      });

    });
  }
}
